/*======================================================================================================================
  * Bluebell: src/app/state/results-view/results-view.reducer.ts
  * Last Edited: 30/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import {
  clearSelectedReportView,
  clearSelectedResultsView,
  createResultsViewSuccess,
  deleteResultsViewSuccess,
  listResultsViews,
  listResultsViewsFailure,
  listResultsViewsSuccess,
  pinResultsViewSuccess,
  reorderPinnedViewsSuccess,
  resultsResolverSelectDefaultView,
  resultsResolverStoreViews,
  selectReportView,
  selectResultsView,
  selectResultsViewFailure,
  selectResultsViewSuccess,
  setCurrentView,
  setCurrentViewColumnConfigs,
  setCurrentViewDeltaBaseline,
  setResultsViewAsProjectDefaultSuccess,
  toggleCurrentViewDeltaToLeadCase,
  updateResultsViewSuccess,
} from '@state/results-view/results-view.actions';
import {
  defaultStandardColumnSortData,
  ResultsColumnSortData,
  ResultsView,
  ResultsViewData,
  ResultsViewListData,
} from '@state/results-view/results-view.interfaces';
import { deleteTextColumnSuccess, updateTextColumnSuccess } from '@state/text-columns/text-columns.actions';
import { TextColumn } from '@state/text-columns/text-columns.interfaces';


export interface State extends EntityState<ResultsViewListData> {
  loading: boolean;
  selectedView: ResultsView|null;
  selectedViewLoading: boolean;
  textColumns: ResultsView['text_columns'];
  columnConfigs: ResultsViewData['columns'];
  deltaBaseline: ResultsViewData['deltaBaseline'] | null;
  deltaToLeadCase: ResultsViewData['deltaToLeadCase'] | null;
  hideEditColumn: boolean;
  sortData: ResultsColumnSortData[];
  reportResultsView: ResultsView | null;
}

export const adapter = createEntityAdapter<ResultsViewListData>({
  selectId: (view: ResultsViewListData) => view.uuid,
  sortComparer: (a: ResultsViewListData, b: ResultsViewListData) => a.description.localeCompare(b.description),
});

export const initialState: State = adapter.getInitialState<State>({
  loading: false,
  selectedView: null,
  selectedViewLoading: false,
  textColumns: [],
  columnConfigs: [],
  deltaBaseline: null,
  deltaToLeadCase: null,
  sortData: [...defaultStandardColumnSortData],
  hideEditColumn: false,
  reportResultsView: null,
});

export const reducer = createReducer<State>(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  /**
   * Create Results View
   */
  on(createResultsViewSuccess, (state, { selectedView }) => {
    const { uuid, description, user, programme_name, programme, default_for_projects, last_used, pinned_in_positions } = selectedView;
    const listData: ResultsViewListData = {
      uuid,
      description,
      user,
      programme_name,
      programme,
      default_for_projects,
      last_used,
      pinned_in_positions,
    };
    return adapter.addOne(listData, { ...state, selectedView });
  }),
  /**
   * Delete Results View
   */
  on(deleteResultsViewSuccess, (state, { uuid }) => {
    return adapter.removeOne(uuid, state);
  }),

  /**
   * List Existing Results Views
   */

  on(listResultsViews, (state) => {
    return { ...state, loading: true };
  }),

  on(listResultsViewsSuccess, (state, { response }) => {
    return adapter.setAll(response.data, { ...state, loading: false });
  }),

  on(resultsResolverStoreViews, (state, { views }) => {
    return adapter.setAll(views, { ...state, loading: false });
  }),

  on(listResultsViewsFailure, (state) => {
    return adapter.removeAll({ ...state, loading: false });
  }),

  /**
   * Select a Results View
   */

  on(selectResultsView, (state) => {
    return { ...state, selectedViewLoading: true };
  }),

  on(
    selectResultsViewSuccess,
    resultsResolverSelectDefaultView,
    updateResultsViewSuccess,
    (state, { selectedView }) => {
      const updates: Update<ResultsView>[] = Object.values(state.entities)
        .filter(view => !!view)
        .map(view => ({
        id: view.uuid,
        changes: {
          last_used: view.uuid === selectedView.uuid
        }
      }));
      return adapter.updateMany(updates, {
        ...state,
        selectedView,
        selectedViewLoading: false,
      });
    }
  ),

  on(setCurrentView, (state, { textColumns, data }) => {
    return {
      ...state,
      textColumns,
      columnConfigs: data.columns || [],
      deltaBaseline: null,
      deltaToLeadCase: data.deltaToLeadCase,
      sortData: data.sortData ?? [...defaultStandardColumnSortData],
      hideEditColumn: data.hideEditColumn ?? false,
    };
  }),

  on(selectResultsViewFailure, (state) => {
    return { ...state, selectedView: null, selectedViewLoading: false };
  }),

  // Resets the entire state except for the list of loaded views
  on(clearSelectedResultsView, (state) => {
    return adapter.setAll(Object.values(state.entities).filter(obj => !!obj), initialState);
  }),

  /**
   * Setting Defaults
   */

  on(pinResultsViewSuccess, setResultsViewAsProjectDefaultSuccess, (state, { updatedView }) => {
    let newState = { ...state };

    if (state.selectedView?.uuid === updatedView.uuid) {
      newState = {
        ...state,
        selectedView: { ...state.selectedView, ...updatedView },
      };
    }

    return adapter.upsertOne(updatedView, newState);
  }),

  /**
   * Manipulate the current view (separate to database)
   */

  on(toggleCurrentViewDeltaToLeadCase, (state) => {
    return { ...state, deltaToLeadCase: !state.deltaToLeadCase };
  }),

  on(setCurrentViewDeltaBaseline, (state, { deltaBaseline }) => {
    return { ...state, deltaBaseline };
  }),

  /**
   * Text Column Actions
   */
  on(deleteTextColumnSuccess, (state, { uuid }) => {
    const filterFunction = (textColumn: TextColumn): boolean => textColumn.uuid !== uuid;
    const textColumns = state.textColumns.filter(filterFunction);

    if (state.selectedView?.text_columns?.length) {
      return {
        ...state,
        textColumns,
        selectedView: {
          ...state.selectedView,
          text_columns: state.selectedView.text_columns.filter(filterFunction),
        },
      };
    } else {
      return { ...state, textColumns };
    }
  }),

  on(updateTextColumnSuccess, (state, { column }) => {
    const mapFunction = (textColumn: TextColumn): TextColumn => textColumn.uuid === column.uuid ? column : textColumn;
    const textColumns = state.textColumns.map(mapFunction);

    if (state.selectedView?.text_columns?.length) {
      return {
        ...state,
        textColumns,
        selectedView: {
          ...state.selectedView,
          text_columns: state.selectedView.text_columns.map(mapFunction),
        },
      };
    } else {
      return {...state, textColumns };
    }
  }),

  on(selectReportView, (state, { reportResultsView }) => {
    return {
      ...state,
      reportResultsView,
      columnConfigs: reportResultsView.data.columns ?? state.columnConfigs
    };
  }),


  on(clearSelectedReportView, (state) => {
    return {
      ...state,
      reportResultsView: null,
      columnConfigs: state.selectedView?.data.columns ?? []
    };
  }),

  on(setCurrentViewColumnConfigs, (state, { columnConfigs }) => {
    return {
      ...state,
      columnConfigs
    };
  }),

  on(reorderPinnedViewsSuccess, (state, { settingsList, projectId }) => {
    const updates: Update<ResultsView>[] = settingsList.reduce(
      (updates: Update<ResultsView>[], settingsId, idx) => {
        const settings = state.entities[settingsId];
        if (settings) {
          updates.push({
            id: settingsId,
            changes: {
              pinned_in_positions: settings.pinned_in_positions.map(
                p => p.project === projectId
                  ? { ...p, position: idx }
                  : p
              )
            }
          });
        }
        return updates;
      },
      []
    );

    return adapter.updateMany(updates, state);
  }),


);

export const featureKey = 'resultsView';
export const {
  selectEntities: getEntities,
  selectAll: getAll,
  selectIds: getIds,
  selectTotal: getCount,
} = adapter.getSelectors();
export const getLoading = (state: State) => state.loading;
export const getSelectedView = (state: State) => state.selectedView;
export const getSelectedViewLoading = (state: State) => state.selectedViewLoading;
export const getTextColumns = (state: State) => state.textColumns;
export const getColumnConfigs = (state: State) => state.columnConfigs || [];
export const getDeltaBaseline = (state: State) => state.deltaBaseline || null;
export const getDeltaToLeadCase = (state: State) => state.deltaToLeadCase || null;
export const getHideEditColumn = (state: State) => state.hideEditColumn;
export const getSortData = (state: State) => state.sortData;
export const getReportResultsView = (state: State) => state.reportResultsView;
